// src/features/route/RouteImportButton.jsx
import React, { useRef } from "react"
import { Button, Typography } from "@mui/material"
import Tooltip from "@mui/material/Tooltip"
import FileUploadIcon from "@mui/icons-material/FileUpload"
import { useAccessContext } from "@/contexts/AccessContext"
import { corePost } from "@/services/coreApi"
import { IMPORT_FILE_ACCEPT } from "@/config/routeExportFormats"

const normalizeJsonText = (text) => {
  let s = text.replace(/:\s*'true'(?=\s*[,\}])/g, ": true").replace(/:\s*'false'(?=\s*[,\}])/g, ": false")
  s = s.replace(
    /("(?:file|path|directory|dir|filepath)"\s*:\s*")([^"]*)(")/g,
    (_m, pre, body, post) => pre + body.replace(/\\/g, "\\\\") + post
  )
  return s
}

const safeJsonParse = (text) => {
  try {
    return JSON.parse(text)
  } catch {
    return JSON.parse(normalizeJsonText(text))
  }
}

const featureCollectionFromBaseRoute = (data) => ({
  type: "FeatureCollection",
  features: (data?.baseRoute?.wps ?? [])
    .map((wp) => {
      const lon = Number(wp?.lon),
        lat = Number(wp?.lat)
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: [lon, lat] },
        properties: {
          name: wp?.name ?? "",
          legtype: wp?.legType ?? null,
          idleTime: wp?.idleTime ?? null,
          fuelType: wp?.fuelType ?? null,
          timestamp: wp?.calculatedTimestamp ? new Date(wp.calculatedTimestamp).toISOString() : null,
          lonlat: [lon, lat],
        },
      }
    })
    .filter(Boolean),
})

// RTZ goes through core; without a schedule (needsSchedule) the form asks for departure and speed.
async function readImportFile(file, accessContext, setAccessContext) {
  const text = await file.text()
  if (!/\.rtz$/i.test(file.name) && !text.trimStart().startsWith("<")) {
    return { format: "voyageFile", data: safeJsonParse(text), needsSchedule: false, text }
  }
  const converted = await corePost(
    "/services/route-import-convert",
    { content: text },
    accessContext,
    setAccessContext,
    ".core /services/route-import-convert error=",
  )
  return { format: converted.format, data: converted.file, needsSchedule: converted.needsSchedule, text }
}

export default function RouteImportButton({
  accept = IMPORT_FILE_ACCEPT,
  buttonLabel = "Import route",
  tooltipTitle = "Import a route file. Vessel and voyage are chosen in the next step.",
  buttonSx = {},
  onDraftReady,
  onError,
}) {
  const inputRef = useRef(null)
  const { accessContext, setAccessContext } = useAccessContext()

  const openDialog = () => inputRef.current?.click()

  const onChange = async (e) => {
    try {
      const file = e.target.files?.[0]
      if (!file) return

      const { format, data, needsSchedule, text } = await readImportFile(file, accessContext, setAccessContext)

      const geojson = featureCollectionFromBaseRoute(data)
      if (!geojson.features.length) throw new Error("No waypoints found in baseRoute.wps")

      const wps = data?.baseRoute?.wps ?? []
      if (!needsSchedule && wps.some((wp) => wp?.calculatedTimestamp == null)) {
        throw new Error("Route file is missing calculatedTimestamp on one or more waypoints and cannot be imported")
      }

      const routeName = data?.baseRoute?.name || `Imported ${file.name.replace(/\.[^.]+$/, "")}`
      // Old exports lack vesselName; the profile name minus the "Default Vessel" placeholder is the best guess.
      const profileName = data?.windAndWaveProfile?.name ?? ""
      const vesselName =
        data?.vesselName ||
        (/^default( vessel)?$/i.test(profileName.trim()) ? "" : profileName)
      const firstWp = wps[0] ?? null
      const lastWp = wps.at(-1) ?? null
      const startTime =
        data?.departureTime ??
        firstWp?.calculatedTimestamp ??
        firstWp?.timestamp ??
        null
      const endTime = lastWp?.calculatedTimestamp ?? lastWp?.timestamp ?? null

      const draft = {
        geojson,
        vesselName,
        vesselImo: String(data?.vesselImo ?? "").trim(),
        routeName,
        waypointCount: wps.length,
        startTime,
        endTime,
        vesselId: data?.vesselId ?? null,
        routeId: data?.routeId ?? `import:${Date.now()}`,
        sourceFileName: file.name,
        sourceData: data,
        sourceFormat: format,
        sourceText: format === "voyageFile" ? null : text,
        needsSchedule,
      }

      onDraftReady?.(draft)
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)))
    } finally {
      if (e?.target) e.target.value = ""
    }
  }

  return (
    <>
      <input type="file" ref={inputRef} onChange={onChange} accept={accept} style={{ display: "none" }} />
      <Tooltip
        title={tooltipTitle}
        arrow
      >
        <Button
          startIcon={<FileUploadIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#004f7d",
            textTransform: "none",
            width: "100%",
            ...buttonSx,
          }}
          onClick={openDialog}
        >
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
            {buttonLabel}
          </Typography>
        </Button>
      </Tooltip>
    </>
  )
}
